<template>
    <div>
        <!-- Backdrop -->
        <transition
            enter-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-300"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-show="isOpen"
                class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                @click="confirmClose"
            ></div>
        </transition>

        <!-- Fullscreen Modal -->
        <transition
            enter-active-class="transform transition-transform duration-600 ease-out"
            enter-from-class="-translate-y-full"
            enter-to-class="translate-y-0"
            leave-active-class="transform transition-transform duration-600 ease-in"
            leave-from-class="translate-y-0"
            leave-to-class="-translate-y-full"
        >
            <aside
                v-if="isOpen"
                class="fixed top-0 left-0 z-50 w-full h-full bg-white shadow-xl overflow-y-auto transform transition-transform"
            >
                <!-- Header -->
                <div class="px-4 py-4 border-b bg-gray-100 sticky top-0 z-10">
                    <!-- Logo & Title -->
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div class="flex items-center gap-3 min-w-0 flex-1">
                            <AppLogo />
                            <h2 class="text-lg font-semibold text-gray-800">
                                <slot name="heading">Panel Title</slot>
                            </h2>
                        </div>

                        <!-- Buttons on larger screens -->
                        <div class="hidden sm:flex items-center gap-3">
                            <t-button
                                v-if="isDirty"
                                variant="success"
                                :disabled="processing"
                                :class="{ 'opacity-50 pointer-events-none': processing }"
                                @click="handleFormSubmit"
                            >
                                {{ processing ? 'Saving...' : 'Save' }}
                            </t-button>
                            <t-button
                                variant="outline"
                                class="hover:text-red-500"
                                @click="confirmClose"
                            >
                                ✕ Close
                            </t-button>
                        </div>
                    </div>

                    <!-- Buttons for small screens -->
                    <div class="flex sm:hidden justify-end gap-3 mt-4">
                        <t-button
                            v-if="isDirty"
                            variant="success"
                            size:xs
                            :disabled="processing"
                            :class="{ 'opacity-50 pointer-events-none': processing }"
                            @click="handleFormSubmit"
                        >
                            {{ processing ? 'Saving...' : 'Save' }}
                        </t-button>
                        <t-button
                            variant="outline"
                            class="hover:text-red-500"
                            @click="confirmClose"
                        >
                            ✕ Close
                        </t-button>
                    </div>
                </div>

                <!-- Content -->
                <div
                    class="p-2 md:pt-6 md:pb-10"
                    v-if="isOpen"
                >
                    <slot v-if="!componentName"> No data passed to render in FullScreenModal. </slot>
                    <component
                        :is="componentName"
                        v-else
                        ref="formRef"
                        :edit-data="editData"
                        @confirmClose="confirmClose"
                        @forceClose="forceClose"
                        @update:isDirty="(val) => (isDirty = val)"
                    />
                </div>

                <!-- Footer -->
                <div class="fixed bottom-0 left-0 w-full border-t bg-white shadow-inner z-10">
                    <div class="flex items-center justify-end gap-5 px-6 py-3">
                        <t-button
                            variant="text"
                            @click="confirmParent"
                        >
                            Go Back
                        </t-button>
                        <t-button
                            variant="text"
                            @click="confirmDashboard"
                        >
                            Dashboard
                        </t-button>
                        <t-button
                            variant="text"
                            @click="confirmLogout"
                        >
                            Logout
                        </t-button>
                    </div>
                </div>
            </aside>
        </transition>
    </div>
</template>

<script>
    import AppLogo from '@/Components/AppLogo.vue';
    import CreateBank from './BankForm.vue';
    import CreateProduct from './ProductForm.vue';
    import CreateCategory from './CategoryForm.vue';
    import CreateBrand from './BrandForm.vue';
    import PromocodeForm from './PromocodeForm.vue';
    import CreateConfig from './ConfigForm.vue';
    import CreateAdmin from './CreateAdmin.vue';
    import CreateSalesperson from './CreateSalesperson.vue';
    import CreateFeaturedImage from './CreateFeaturedImage.vue';
    import CreateProductFeaturedImage from './CreateProductFeaturedImage.vue';

    export default {
        name: 'FullScreenModal',

        components: {
            AppLogo,
            CreateProduct,
            CreateCategory,
            PromocodeForm,
            CreateBrand,
            CreateBank,
            CreateConfig,
            CreateFeaturedImage,
            CreateProductFeaturedImage,
            CreateAdmin,
            CreateSalesperson,
        },

        props: {
            isOpen: {
                type: Boolean,
                default: false,
            },
            componentName: {
                type: [String, Object],
                default: null,
            },
            editData: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                isDirty: false,
                processing: false, // for loading state
            };
        },

        watch: {
            isOpen(val) {
                document.body.classList.toggle('overflow-hidden', val);
                if (!val) this.isDirty = false;
            },
        },

        methods: {
            forceClose() {
                //this.$emit('set-processing', false);
                this.$emit('closeRightSidebar');
                this.processing = false;
            },
            async confirmClose() {
                if (this.isDirty) {
                    let result;

                    if (this.$dialog?.confirm) {
                        result = await this.$dialog.confirm({
                            title: 'Confirm cancel',
                            text: 'Unsaved changes will be lost. Proceed to parent page?',
                        });

                        if (result.isOk) {
                            this.$emit('closeRightSidebar');
                        }
                    } else {
                        // Fallback to native confirm
                        const confirmed = window.confirm('Unsaved changes will be lost. Do you want to continue?');
                        if (confirmed) {
                            this.$emit('closeRightSidebar');
                        }
                    }
                } else {
                    this.$emit('closeRightSidebar');
                }
            },
            async confirmParent() {
                if (this.isDirty) {
                    let result;

                    if (this.$dialog?.confirm) {
                        result = await this.$dialog.confirm({
                            title: 'Confirm to go Back',
                            text: 'Unsaved changes will be lost. Proceed to parent page?',
                        });

                        if (result.isOk) {
                            this.goToParent();
                        }
                    } else {
                        // Fallback to native confirm
                        const confirmed = window.confirm('Unsaved changes will be lost. Do you want to continue?');
                        if (confirmed) {
                            this.goToParent();
                        }
                    }
                } else {
                    this.goToParent();
                }
            },
            async confirmDashboard() {
                if (this.isDirty) {
                    let result;

                    if (this.$dialog?.confirm) {
                        result = await this.$dialog.confirm({
                            title: 'Confirm to go Dashboard',
                            text: 'Unsaved changes will be lost. Proceed to dashboard?',
                        });

                        if (result.isOk) {
                            this.goToDashboard();
                        }
                    } else {
                        // Fallback to native confirm
                        const confirmed = window.confirm('Unsaved changes will be lost. Do you want to continue?');
                        if (confirmed) {
                            this.goToDashboard();
                        }
                    }
                } else {
                    this.goToDashboard();
                }
            },
            async confirmLogout() {
                if (this.isDirty) {
                    let result;

                    if (this.$dialog?.confirm) {
                        result = await this.$dialog.confirm({
                            title: 'Confirm Logout',
                            text: 'Unsaved changes will be lost. Proceed to logout?',
                        });

                        if (result.isOk) {
                            this.logout();
                        }
                    } else {
                        // Fallback to native confirm
                        const confirmed = window.confirm('Unsaved changes will be lost. Do you want to continue?');
                        if (confirmed) {
                            this.logout();
                        }
                    }
                } else {
                    this.logout();
                }
            },
            goToDashboard() {
                this.$inertia.visit('/dashboard');
            },
            goToParent() {
                this.$inertia.visit(this.$page.props.parent_url || '/');
            },
            logout() {
                this.$inertia.post(route('logout'));
            },

            async handleFormSubmit() {
                if (this.processing) return; // Prevent re-entry

                const confirmed =
                    (await this.$dialog?.confirm({
                        title: 'Confirm Save',
                        text: 'Are you sure you want to save this product?',
                    })) ?? window.confirm('Are you sure you want to save this product?');

                // Handle result safely
                if (confirmed?.isCancel || confirmed === false || confirmed?.isDismissed) return;

                this.processing = true;
                // this.$emit('set-processing', true);

                try {
                    if (this.$refs.formRef?.submitForm) {
                        await this.$refs.formRef.submitForm();
                    } else {
                        console.warn('submitForm method not found on child component');
                    }
                } catch (e) {
                    console.error('Error submitting form', e);
                } finally {
                    this.processing = false;
                }
            },
        },
    };
</script>
